import React, { useRef, useState, useEffect } from 'react'
import './ToolPalette.css'

interface Tool {
  id: string
  name: string
  icon: string
  category: 'basic' | 'advanced'
}

const tools: Tool[] = [
  { id: 'select', name: 'Select', icon: '👆', category: 'basic' },
  { id: 'point', name: 'Point', icon: '📍', category: 'basic' },
  { id: 'measure', name: 'Measure', icon: '📏', category: 'basic' },
  { id: 'move', name: 'Move', icon: '↔️', category: 'basic' },
  { id: 'rotate', name: 'Rotate', icon: '🔄', category: 'basic' },
  { id: 'scale', name: 'Scale', icon: '🔍', category: 'basic' },
  { id: 'export-stl', name: 'Export STL', icon: '📤', category: 'advanced' },
  { id: 'generate-insole', name: 'Classic Insole', icon: '🩴', category: 'advanced' },
  { id: 'v2-pipeline', name: 'V2 Pipeline', icon: '🏗️', category: 'advanced' },
]

interface ToolPaletteProps {
  selectedTool: string | null
  onToolSelect: (toolId: string) => void
}

export const ToolPalette: React.FC<ToolPaletteProps> = ({
  selectedTool,
  onToolSelect,
}) => {
  const basicTools = tools.filter(tool => tool.category === 'basic')
  const advancedTools = tools.filter(tool => tool.category === 'advanced')

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  // Check scroll position to enable/disable scroll buttons
  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    setCanScrollUp(scrollTop > 0)
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1)
  }

  // Check scroll position on mount and when content changes
  useEffect(() => {
    checkScrollPosition()

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollPosition)
      // Also check on resize
      const resizeObserver = new ResizeObserver(checkScrollPosition)
      resizeObserver.observe(container)

      return () => {
        container.removeEventListener('scroll', checkScrollPosition)
        resizeObserver.disconnect()
      }
    }
  }, [])

  const scrollUp = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: -100,
        behavior: 'smooth'
      })
    }
  }

  const scrollDown = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: 100,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="tool-palette">
      {/* Scroll Up Button */}
      <button
        className={`scroll-btn scroll-up ${!canScrollUp ? 'disabled' : ''}`}
        onClick={scrollUp}
        disabled={!canScrollUp}
        title="Scroll up"
      >
        ▲
      </button>

      {/* Scrollable Container */}
      <div className="tool-palette-scroll-container" ref={scrollContainerRef}>
        <div className="tool-section">
          <div className="tool-section-header">Tools</div>
          <div className="tool-grid">
            {basicTools.map(tool => (
              <button
                key={tool.id}
                className={`tool-btn ${selectedTool === tool.id ? 'selected' : ''}`}
                onClick={() => onToolSelect(tool.id)}
                title={tool.name}
              >
                <span className="tool-icon">{tool.icon}</span>
                <span className="tool-name">{tool.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="tool-section">
          <div className="tool-section-header">Advanced</div>
          <div className="tool-grid">
            {advancedTools.map(tool => (
              <button
                key={tool.id}
                className={`tool-btn ${selectedTool === tool.id ? 'selected' : ''}`}
                onClick={() => onToolSelect(tool.id)}
                title={tool.name}
              >
                <span className="tool-icon">{tool.icon}</span>
                <span className="tool-name">{tool.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      <button
        className={`scroll-btn scroll-down ${!canScrollDown ? 'disabled' : ''}`}
        onClick={scrollDown}
        disabled={!canScrollDown}
        title="Scroll down"
      >
        ▼
      </button>
    </div>
  )
}
